/**
 * The web PostHog wrapper (ANALYTICS.md §1): the ONLY file in apps/client that imports `posthog-js`. It is
 * loaded lazily, so a player who never opts in never downloads or initialises the SDK. Created OFF
 * (`opt_out_capturing_by_default`), IP discard, no autocapture / pageviews / session recording / surveys / flags /
 * exception capture, no person profiles, localStorage persistence (no cookies).
 */
import type { PostHog } from 'posthog-js';
import type { AnalyticsDriver, AnalyticsValue } from '@hf/client-core/analytics';

export interface WebPostHogConfig {
  key: string;
  host: string;
}

export const createWebDriver = (cfg: WebPostHogConfig): AnalyticsDriver => {
  let loading: Promise<PostHog> | null = null;
  let registered: Record<string, AnalyticsValue> = {};
  const load = (): Promise<PostHog> =>
    (loading ??= import('posthog-js').then(({ default: posthog }) => {
      posthog.init(cfg.key, {
        api_host: cfg.host,
        opt_out_capturing_by_default: true,
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        disable_session_recording: true,
        disable_surveys: true,
        advanced_disable_flags: true,
        capture_exceptions: false,
        person_profiles: 'never',
        persistence: 'localStorage',
        ip: false,
        respect_dnt: true,
      } as Parameters<typeof posthog.init>[1]);
      posthog.register(registered);
      return posthog;
    }));
  return {
    // Nothing is loaded or sent until the player opts in.
    init: () => undefined,
    register(props) {
      registered = { ...registered, ...props };
      void loading?.then((ph) => ph.register(props));
    },
    optIn() {
      void load().then((ph) => ph.opt_in_capturing({ captureEventName: false }));
    },
    optOut() {
      void loading?.then((ph) => ph.opt_out_capturing());
    },
    capture(event, props) {
      void loading?.then((ph) => ph.capture(event, props));
    },
    identify(id) {
      void loading?.then((ph) => ph.identify(id));
    },
  };
};
